import { useDereferenceMedia, useMedia, useReferenceMedia } from "@/hooks/contextHooks"
import { useMemo } from "react"
import FuzzyFinder, { FuzzyFindWrapperProps } from "./fuzzyFinder"
import {
	Flex,
	Heading,
	SimpleGrid,
	Text,
	Textarea,
	Button,
	Box,
	Center,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalCloseButton,
	ModalBody,
	ModalFooter,
	useDisclosure,
	useColorModeValue
} from "@chakra-ui/react"
import MediaSingleSelectionCard from "./mediaSingleSelectionCard"
import { MediaMeta, MediaType } from "@/pages/mediaManager"
import { ReactStateType } from "@xnav/types/src/frontend"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle, faTrash, faUpload, faFilePdf, faImage } from "@fortawesome/free-solid-svg-icons"
import { EditContent } from "@/pages/editor"

const renderWrapper = <T,>({ data, renderComponentProps, RenderComponent }: FuzzyFindWrapperProps<T>) => (
	<SimpleGrid padding={3} w='full' flex={1} minChildWidth='300px' spacing={3}>
		{data.map((d, i) => <RenderComponent key={i} data={d} {...renderComponentProps} />)}
	</SimpleGrid>
)

const VideoInfoEditor: React.FC<{
	content: EditContent,
	setContent: React.Dispatch<React.SetStateAction<EditContent | undefined>>
}> = ({ content, setContent }) => {
	const referenceMedia = useReferenceMedia()
	const dereferenceMedia = useDereferenceMedia()
	const [media] = useMedia() as ReactStateType<MediaMeta[]>
	const { isOpen, onOpen, onClose } = useDisclosure()

	const infoMediaItems = useMemo(() => {
		if (!media) return []
		return media.filter(m => m.type === MediaType.Image || m.type === MediaType.Pdf)
	}, [media])

	const selectedMedia = useMemo(() => {
		if (!content.infoMediaId || !media) return null
		return media.find(m => m.id === content.infoMediaId) || null
	}, [media, content.infoMediaId])

	const cardBg = useColorModeValue("white", "gray.800")
	const borderColor = useColorModeValue("gray.200", "gray.700")

	return (
		<Flex flexDir='column' w='100%' h='100%' p={6} gap={6} overflowY="auto">
			<Box>
				<Heading size="md" mb={1} display="flex" alignItems="center" gap={2}>
					<FontAwesomeIcon icon={faInfoCircle} color="#319795" />
					Eye Button Info Overlay
				</Heading>
				<Text fontSize="sm" color="gray.500">
					Add context, notes, images, or PDFs that will appear when a user clicks the "Eye" button on the mobile app.
				</Text>
			</Box>

			{/* Description Area */}
			<Flex flexDir="column" gap={2}>
				<Text fontWeight="semibold" fontSize="sm">Description Text</Text>
				<Textarea
					placeholder="Enter explanation text or clinical notes..."
					value={content.infoText || ""}
					onChange={(e) => setContent({ ...content, infoText: e.target.value })}
					size="md"
					borderRadius="xl"
					rows={6}
					focusBorderColor="teal.400"
					bg={cardBg}
				/>
			</Flex>

			{/* Attached Media Area */}
			<Flex flexDir="column" gap={2}>
				<Text fontWeight="semibold" fontSize="sm">Attached Image or PDF</Text>
				{selectedMedia ? (
					<Flex 
						p={4} 
						borderRadius="2xl" 
						border="1px solid" 
						borderColor={borderColor} 
						bg={cardBg} 
						align="center" 
						justify="space-between"
						shadow="sm"
					>
						<Flex align="center" gap={3}>
							<Flex 
								align="center" 
								justify="center" 
								boxSize="48px" 
								borderRadius="xl" 
								bg={selectedMedia.type === MediaType.Pdf ? "red.50" : "blue.50"} 
								color={selectedMedia.type === MediaType.Pdf ? "red.500" : "blue.500"}
								_dark={{ bg: selectedMedia.type === MediaType.Pdf ? "rgba(229, 62, 62, 0.15)" : "rgba(66, 153, 225, 0.15)" }}
							>
								<FontAwesomeIcon 
									icon={selectedMedia.type === MediaType.Pdf ? faFilePdf : faImage} 
									size="lg" 
								/>
							</Flex>
							<Box>
								<Text fontWeight="bold" fontSize="sm" noOfLines={1}>{selectedMedia.name}</Text>
								<Text fontSize="xs" color="gray.500">
									{selectedMedia.type === MediaType.Pdf ? "PDF Document" : "Image File"}
								</Text>
							</Box>
						</Flex>
						<Button 
							size="sm" 
							colorScheme="red" 
							variant="ghost" 
							leftIcon={<FontAwesomeIcon icon={faTrash} />}
							onClick={() => {
								if (content.infoMediaId) {
									dereferenceMedia(content.infoMediaId)
								}
								const removedTitle = content.infoMediaTitle
								const updatedWisdomDocs = removedTitle
									? (content.wisdomDocTitles || []).filter(t => t !== removedTitle)
									: content.wisdomDocTitles || []

								setContent({ 
									...content, 
									infoMediaId: undefined, 
									infoMediaTitle: undefined,
									infoMediaUrl: undefined,
									infoPdfUrl: undefined,
									wisdomDocTitles: updatedWisdomDocs
								})
							}}
						>
							Remove
						</Button>
					</Flex>
				) : (
					<Center 
						p={8} 
						borderRadius="2xl" 
						border="2px dashed" 
						borderColor={borderColor} 
						cursor="pointer" 
						_hover={{ borderColor: "teal.400", bg: useColorModeValue("teal.50", "rgba(49, 151, 149, 0.05)") }} 
						transition="all 0.2s"
						onClick={onOpen}
					>
						<Flex flexDir="column" align="center" gap={2}>
							<FontAwesomeIcon icon={faUpload} size="lg" color="#a0aec0" />
							<Text fontSize="sm" fontWeight="medium" color="gray.600" _dark={{ color: "gray.400" }}>
								Attach Image or PDF from Media Manager
							</Text>
						</Flex>
					</Center>
				)}
			</Flex>

			{/* Selector Modal */}
			<Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside" isCentered>
				<ModalOverlay backdropFilter="blur(5px)" />
				<ModalContent borderRadius="3xl" overflow="hidden">
					<ModalHeader>Attach Image or PDF</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<FuzzyFinder
							data={infoMediaItems}
							searchKeys={['name', 'type']}
							RenderComponent={MediaSingleSelectionCard}
							renderComponentProps={{
								onSelect: (mediaRes: MediaMeta) => {
									if (content.infoMediaId) {
										dereferenceMedia(content.infoMediaId)
									}
									referenceMedia(mediaRes.id)

									const isPdf = mediaRes.type === MediaType.Pdf || mediaRes.name.toLowerCase().endsWith('.pdf')
									const currentWisdomDocs = content.wisdomDocTitles || []
									const updatedWisdomDocs = isPdf && !currentWisdomDocs.includes(mediaRes.name)
										? [...currentWisdomDocs, mediaRes.name]
										: currentWisdomDocs

									setContent({
										...content,
										infoMediaId: mediaRes.id,
										infoMediaTitle: mediaRes.name,
										infoMediaUrl: mediaRes.fileUrl,
										infoPdfUrl: isPdf ? mediaRes.fileUrl : undefined,
										wisdomDocTitles: updatedWisdomDocs
									})
									onClose()
								}
							}}
							RenderWrapper={renderWrapper}
						>
							<></>
						</FuzzyFinder>
					</ModalBody>
					<ModalFooter borderTop="1px solid" borderColor={borderColor}>
						<Button variant="ghost" onClick={onClose} borderRadius="xl">Cancel</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</Flex>
	)
}

export default VideoInfoEditor
